// Amaya Stories Site Configuration

export const CREDIT_COSTS = {
  coloringBook: 1,
  audioBook: 3,
} as const;

export const CREDIT_PACKS = [
  { id: 'credits10', name: 'Starter', credits: 10, priceUsd: 15, env: 'STRIPE_PRICE_10CREDITS' },
  { id: 'credits25', name: 'Family',  credits: 25, priceUsd: 30, env: 'STRIPE_PRICE_25CREDITS', featured: true },
  { id: 'credits60', name: 'Pro',     credits: 60, priceUsd: 60, env: 'STRIPE_PRICE_60CREDITS' },
] as const;

export const SUBSCRIPTIONS = [
  {
    id: 'amaya_plus',
    name: 'Amaya Plus',
    priceCents: 1999,
    monthlyCreditsEnv: 'SUBS_PLUS_MONTHLY_CREDITS',
    priceEnv: 'STRIPE_PRICE_SUB_AMAYA_PLUS_MONTHLY',
    perks: [
      '100+ Audio Story Gallery',
      'New stories added daily',
      'Cancel anytime',
    ],
    queueWeightEnv: 'QUEUE_WEIGHT_SUBSCRIBER_PLUS',
    earlyAccess: false,
    voiceSelection: false,
  },
  {
    id: 'amaya_pro',
    name: 'Amaya Pro',
    priceCents: 4999,
    monthlyCreditsEnv: 'SUBS_PRO_MONTHLY_CREDITS',
    priceEnv: 'STRIPE_PRICE_SUB_AMAYA_PRO_MONTHLY',
    perks: [
      'Everything in Plus',
      'Priority generation (2× faster)',
      'Change voices for Audio books',
      'Early access to experimental models',
    ],
    queueWeightEnv: 'QUEUE_WEIGHT_SUBSCRIBER_PRO',
    earlyAccess: true,
    voiceSelection: true,
  },
] as const;

export const FEATURE_FLAGS = {
  gallery: process.env.FEATURE_GALLERY_ENABLED === 'true',
  priority: process.env.FEATURE_PRIORITY_GENERATION === 'true',
  voices: process.env.FEATURE_VOICE_SELECTION === 'true',
  earlyAccess: process.env.FEATURE_EARLY_ACCESS_FLAG === 'true',
  seoGeneration: process.env.ENABLE_SEO_GENERATION_WITH_OPENAI === 'true',
  structuredData: process.env.ENABLE_STRUCTURED_DATA === 'true',
  audiobookSeoEnhancements: process.env.ENABLE_AUDIOBOOK_SEO_ENHANCEMENTS === 'true',
  // LLM-First Architecture flags
  llmFirstArchitecture: process.env.ENABLE_LLM_FIRST_ARCHITECTURE === 'true',
  llmManifests: process.env.ENABLE_LLM_MANIFESTS === 'true',
  llmContentApi: process.env.ENABLE_LLM_CONTENT_API === 'true',
  // Advanced SEO Extensions
  eeatLayer: process.env.ENABLE_EEAT_LAYER === 'true',
  topicalClusters: process.env.ENABLE_TOPICAL_CLUSTERS === 'true',
  entitySeo: process.env.ENABLE_ENTITY_SEO === 'true',
  llmOptimization: process.env.ENABLE_LLM_OPTIMIZATION === 'true',
} as const;

export const SITE_CONFIG = {
  name: 'Amaya Stories',
  domain: 'amayastories.com',
  url: process.env.APP_URL || 'https://www.amayastories.com',
  siteUrl: process.env.APP_URL || 'https://www.amayastories.com', // Alias for url
  description: 'Create magical AI-powered coloring books, bilingual audio stories, and language learning adventures for children',
  tagline: 'Bring Your Child\'s Imagination to Life',
  
  // Organization details for structured data
  organization: {
    name: 'Amaya Stories',
    logo: 'https://www.amayastories.com/logo.png',
    contactPoint: {
      email: process.env.CONTACT_EMAIL || 'info@amayastories.com',
    },
  },
  
  // Blog author details
  blogAuthor: {
    name: 'Amaya Stories Team',
    url: 'https://www.amayastories.com/about',
  },
  
  // Generation Settings
  freePreviewPages: parseInt(process.env.FREE_PREVIEW_PAGES || '3', 10), // 1 cover + 2 coloring pages
  fullBookPages: parseInt(process.env.FULL_BOOK_PAGES || '13', 10), // 1 cover + 12 pages
  freeRateLimitPerIpPerDay: parseInt(process.env.FREE_RATE_LIMIT_PER_IP_PER_DAY || '10', 10),
  watermarkText: process.env.WATERMARK_TEXT || 'PREVIEW • GENERATED WITH AMAYA STORIES',
  
  // Age Buckets
  ageBuckets: (process.env.AGE_BUCKETS || '0-1,1-2,3-4,5-6,7-8,9-10').split(','),
  
  // Admin
  adminEmails: (process.env.ADMIN_EMAILS || '').split(',').filter(Boolean),
  
  // Default Hashtags
  defaultHashtags: [
    '#animals', '#adventure', '#fantasy', '#space', '#underwater',
    '#dinosaurs', '#princess', '#superhero', '#nature', '#vehicles',
    '#magic', '#forest', '#ocean', '#castle', '#robot'
  ],
  
  // Deprecated pricing kept for backward compatibility, prefer CREDIT_PACKS & SUBSCRIPTIONS
  // NOTE: credits20/credits50 names are legacy - they map to 25/60 credits respectively
  // This object is kept for backward compatibility with old code that may reference it
  pricing: {
    credits10: { credits: 10, price: 1500, priceId: process.env.STRIPE_PRICE_10CREDITS },
    credits20: { credits: 25, price: 3000, priceId: process.env.STRIPE_PRICE_25CREDITS }, // Actually 25 credits
    credits50: { credits: 60, price: 6000, priceId: process.env.STRIPE_PRICE_60CREDITS }, // Actually 60 credits
    unlimited: { price: 1999, priceId: process.env.STRIPE_PRICE_SUB_AMAYA_PLUS_MONTHLY },
  },
  
  // Preview expiration (48 hours)
  previewExpirationHours: 48,
  
  // Audio Book Settings
  audioBook: {
    freePreviewPages: 2,
    fullBookPages: parseInt(process.env.AUDIO_BOOK_PAGES || '12', 10), // Default 12 pages (admin can override up to 50)
    maxAdminPages: 50, // Maximum pages admin can request (for YouTube videos)
    creditCost: CREDIT_COSTS.audioBook,
    previewCreditCost: 0,
  },
} as const;

export const isAdmin = (email: string | null | undefined): boolean => {
  if (!email) return false;
  return SITE_CONFIG.adminEmails.includes(email);
};

export const formatPrice = (cents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

export const AUDIO_BOOK_LANGUAGES = [
  { value: 'en', label: 'English', flag: '🇺🇸', proOnly: false, featured: true },
  { value: 'de', label: 'German', flag: '🇩🇪', proOnly: true, featured: true },
  { value: 'es', label: 'Spanish', flag: '🇪🇸', proOnly: true, featured: true },
  { value: 'it', label: 'Italian', flag: '🇮🇹', proOnly: true, featured: false },
  { value: 'fr', label: 'French', flag: '🇫🇷', proOnly: true, featured: false },
  { value: 'pt', label: 'Portuguese', flag: '🇧🇷', proOnly: true, featured: false },
  { value: 'nl', label: 'Dutch', flag: '🇳🇱', proOnly: true, featured: false },
  { value: 'ja', label: 'Japanese', flag: '🇯🇵', proOnly: true, featured: false },
  { value: 'ko', label: 'Korean', flag: '🇰🇷', proOnly: true, featured: false },
  { value: 'zh', label: 'Mandarin', flag: '🇨🇳', proOnly: true, featured: false },
  { value: 'ar', label: 'Arabic', flag: '🇸🇦', proOnly: true, featured: false },
  { value: 'pl', label: 'Polish', flag: '🇵🇱', proOnly: true, featured: false },
  { value: 'lt', label: 'Lithuanian', flag: '🇱🇹', proOnly: true, featured: false },
] as const;

export type AudioBookLanguage = typeof AUDIO_BOOK_LANGUAGES[number]['value'];

export const FEATURED_LANGUAGES = AUDIO_BOOK_LANGUAGES.filter(l => l.featured);
export const OTHER_LANGUAGES = AUDIO_BOOK_LANGUAGES.filter(l => !l.featured);

// 2026 Award-Winning UI Feature Flag (LOCAL ONLY)
// 2026 UI is now the default (feature flag removed)
export const SITE_UI_2026 = true;

// Audio Library Configuration
export const AUDIO_CONFIG = {
  useLibrarySfx: process.env.AUDIO_USE_LIBRARY_SFX !== 'false', // default true
  useLibraryBgm: process.env.AUDIO_USE_LIBRARY_BGM !== 'false', // default true
  libraryVersion: process.env.AUDIO_LIBRARY_VERSION || 'v1',
  objectReplayEnabled: process.env.AUDIO_OBJECT_REPLAY_ENABLED !== 'false',
  wordHighlightEnabled: process.env.AUDIO_WORD_HIGHLIGHT_ENABLED !== 'false',
  defaultBgmVolume: parseFloat(process.env.AUDIO_DEFAULT_BGM_VOLUME || '0.18'),
  objectReplayVolume: parseFloat(process.env.AUDIO_OBJECT_REPLAY_VOLUME || '0.9'),
  ambientVolume: parseFloat(process.env.AUDIO_AMBIENT_VOLUME || '0.3'),
} as const;

